import React from "react";
import { v4 as uuid } from "uuid";
import { ContactPicker } from "../ContactPicker";
import { ContactsViewer } from "../ContactsViewer";
import { ShapePicker } from "../ShapePicker";
import type { EdgeData, NodeData } from "../../lib/api";

type InspectorProps = {
  viewOnly: boolean;
  isBulk: boolean;
  selectedNodesCount: number;
  selectedEdgesCount: number;
  selectedEdge: any;
  selectedNode: any;
  inspectorTab: "general" | "contacts" | "links" | "notes";
  inspectorTabs: { id: "general" | "contacts" | "links" | "notes"; label: string }[];
  bulkNodeType: string;
  setBulkNodeType: (value: string) => void;
  bulkNodeColor: string;
  setBulkNodeColor: (value: string) => void;
  bulkTextColor: string;
  setBulkTextColor: (value: string) => void;
  bulkEdgeColor: string;
  setBulkEdgeColor: (value: string) => void;
  nodeTemplates: { id: string; label: string; defaultColor?: string; defaultTextColor?: string; shape?: string }[];
  nodeTypeById: (id: string) => any;
  currentNodeData: NodeData | null;
  draftDirty: boolean;
  structureId?: string;
  structureTypeConfig: any;
  setInspectorTab: (tab: "general" | "contacts" | "links" | "notes") => void;
  applyBulkNodes: (patch: Partial<NodeData>) => void;
  applyBulkEdges: (patch: Partial<EdgeData>) => void;
  deleteSelection: () => void;
  updateSelectedEdge: (patch: Partial<EdgeData>) => void;
  primarySelectedEdgeId: string | null;
  setConfirmDialog: (dialog: any) => void;
  setRfEdges: any;
  setSelectedEdgeIds: (ids: string[]) => void;
  setDirty: (val: boolean) => void;
  updateDraftNode: (fn: (prev: NodeData) => NodeData) => void;
  updateSelectedNode: (patch: Partial<NodeData>) => void;
  applyDraftNode: () => void;
  setDraftNode: (node: NodeData | null) => void;
  setDraftDirty: (val: boolean) => void;
};

export function Inspector({
  viewOnly,
  isBulk,
  selectedNodesCount,
  selectedEdgesCount,
  selectedEdge,
  selectedNode,
  inspectorTab,
  inspectorTabs,
  bulkNodeType,
  setBulkNodeType,
  bulkNodeColor,
  setBulkNodeColor,
  bulkTextColor,
  setBulkTextColor,
  bulkEdgeColor,
  setBulkEdgeColor,
  nodeTemplates,
  nodeTypeById,
  currentNodeData,
  draftDirty,
  structureId,
  structureTypeConfig,
  setInspectorTab,
  applyBulkNodes,
  applyBulkEdges,
  deleteSelection,
  updateSelectedEdge,
  primarySelectedEdgeId,
  setConfirmDialog,
  setRfEdges,
  setSelectedEdgeIds,
  setDirty,
  updateDraftNode,
  updateSelectedNode,
  applyDraftNode,
  setDraftNode,
  setDraftDirty,
}: InspectorProps) {
  const hasSelection = selectedNodesCount > 0 || selectedEdgesCount > 0;

  if (viewOnly || !hasSelection) return null;

  if (isBulk) {
    return (
      <div className="w-80 border-l border-[var(--color-border)] dark:border-slate-800 p-4 bg-[var(--color-panel)] backdrop-blur space-y-4 h-[calc(100vh-64px)] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black title-font">Mehrfachauswahl</h3>
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">
            {selectedNodesCount} Nodes / {selectedEdgesCount} Edges
          </span>
        </div>
        {selectedNodesCount > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-[var(--color-muted)]">Nodes ({selectedNodesCount})</div>
            <button
              className="w-full px-3 py-2 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600"
              onClick={deleteSelection}
              disabled={viewOnly}
            >
              Löschen
            </button>
            <div className="space-y-2 text-sm">
              <label className="text-xs text-[var(--color-muted)]">Typ</label>
              <select
                className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                value={bulkNodeType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  setBulkNodeType(nextType);
                  const tpl = nodeTypeById(nextType);
                  const patch: Partial<NodeData> = { type: nextType };
                  if (tpl?.defaultColor) patch.color = tpl.defaultColor;
                  if (tpl?.defaultTextColor) patch.text_color = tpl.defaultTextColor;
                  applyBulkNodes(patch);
                }}
                disabled={viewOnly}
              >
                <option value="" disabled>
                  Auswählen...
                </option>
                {nodeTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
              <label className="text-xs text-[var(--color-muted)]">Farbe</label>
              <input
                type="color"
                className="w-full h-10 rounded-md border border-[var(--color-border)]"
                value={bulkNodeColor}
                onChange={(e) => setBulkNodeColor(e.target.value)}
                onBlur={() => applyBulkNodes({ color: bulkNodeColor })}
                disabled={viewOnly}
              />
              <label className="text-xs text-[var(--color-muted)]">Textfarbe</label>
              <input
                type="color"
                className="w-full h-10 rounded-md border border-[var(--color-border)]"
                value={bulkTextColor}
                onChange={(e) => setBulkTextColor(e.target.value)}
                onBlur={() => applyBulkNodes({ text_color: bulkTextColor })}
                disabled={viewOnly}
              />
              <div className="flex flex-wrap gap-2 text-xs">
                <button
                  className="px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)]"
                  onClick={() => applyBulkNodes({ inactive: true })}
                  disabled={viewOnly}
                >
                  Inaktiv setzen
                </button>
                <button
                  className="px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)]"
                  onClick={() => applyBulkNodes({ inactive: false })}
                  disabled={viewOnly}
                >
                  Aktiv setzen
                </button>
                <button
                  className="px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)]"
                  onClick={() => applyBulkNodes({ constituted: true })}
                  disabled={viewOnly}
                >
                  Konstituiert an
                </button>
                <button
                  className="px-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)]"
                  onClick={() => applyBulkNodes({ constituted: false })}
                  disabled={viewOnly}
                >
                  Konstituiert aus
                </button>
              </div>
            </div>
          </div>
        )}
        {selectedEdgesCount > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold text-[var(--color-muted)]">Edges ({selectedEdgesCount})</div>
            <button
              className="w-full px-3 py-2 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600"
              onClick={deleteSelection}
              disabled={viewOnly}
            >
              Löschen
            </button>
            <div className="space-y-2 text-sm">
              <label className="text-xs text-[var(--color-muted)]">Linienform</label>
              <select
                className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                onChange={(e) => applyBulkEdges({ line_style: e.target.value as EdgeData["line_style"] })}
                defaultValue=""
                disabled={viewOnly}
              >
                <option value="" disabled>
                  Auswählen...
                </option>
                <option value="straight">Straight</option>
                <option value="step">Step</option>
                <option value="bezier">Bezier</option>
                <option value="smoothstep">Smoothstep</option>
              </select>
              <label className="text-xs text-[var(--color-muted)]">Pfeile</label>
              <select
                className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                onChange={(e) => applyBulkEdges({ arrow_style: e.target.value as EdgeData["arrow_style"] })}
                defaultValue=""
                disabled={viewOnly}
              >
                <option value="" disabled>
                  Auswählen...
                </option>
                <option value="default">Default</option>
                <option value="reverse">Reverse</option>
                <option value="both">Both</option>
                <option value="none">None</option>
              </select>
              <label className="text-xs text-[var(--color-muted)]">Farbe</label>
              <input
                type="color"
                className="w-full h-10 rounded-md border border-[var(--color-border)]"
                value={bulkEdgeColor}
                onChange={(e) => setBulkEdgeColor(e.target.value)}
                onBlur={() => applyBulkEdges({ color: bulkEdgeColor })}
                disabled={viewOnly}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (selectedEdge) {
    return (
      <div className="w-80 border-l border-[var(--color-border)] dark:border-slate-800 p-4 bg-[var(--color-panel)] backdrop-blur space-y-3 h-[calc(100vh-64px)] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-black title-font">Edge</h3>
          <span className="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">Verbindung</span>
        </div>
        <label className="text-xs text-[var(--color-muted)]">Arrow Style</label>
        <select
          className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
          value={(selectedEdge.data as EdgeData)?.arrow_style || "default"}
          onChange={(e) => updateSelectedEdge({ arrow_style: e.target.value as EdgeData["arrow_style"] })}
          disabled={viewOnly}
        >
          <option value="default">Default</option>
          <option value="reverse">Reverse</option>
          <option value="both">Both</option>
          <option value="none">None</option>
        </select>
        <label className="text-xs text-[var(--color-muted)]">Farbe</label>
        <input
          type="color"
          className="w-full h-10 mt-1 mb-3"
          value={(selectedEdge.data as any)?.color || "#64748b"}
          disabled={viewOnly}
          onChange={(e) => updateSelectedEdge({ color: e.target.value })}
        />
        <label className="text-xs text-[var(--color-muted)]">Linienform</label>
        <select
          className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
          value={(selectedEdge.data as any)?.line_style || "bezier"}
          disabled={viewOnly}
          onChange={(e) => updateSelectedEdge({ line_style: e.target.value as any })}
        >
          <option value="bezier">Geschwungen (Bezier)</option>
          <option value="straight">Gerade</option>
          <option value="step">Eckig (90°)</option>
          <option value="smoothstep">Eckig abgerundet</option>
        </select>
        {((selectedEdge.data as any)?.line_style || "bezier") === "step" && (
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-xs text-[var(--color-muted)]">
              <span>Routing</span>
              <select
                className="rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-xs"
                value={(selectedEdge.data as any)?.routing?.mode || "auto"}
                disabled={viewOnly}
                onChange={(e) =>
                  updateSelectedEdge({
                    routing: {
                      ...(selectedEdge.data as any)?.routing,
                      mode: e.target.value as "auto" | "manual",
                    },
                  })
                }
              >
                <option value="auto">Auto</option>
                <option value="manual">Manuell</option>
              </select>
            </div>
            {((selectedEdge.data as any)?.routing?.mode || "auto") === "manual" && (
              <div className="space-y-1">
                <label className="text-xs text-[var(--color-muted)]">Knick-Offset (px)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={-400}
                    max={400}
                    step={10}
                    className="flex-1"
                    value={(selectedEdge.data as any)?.routing?.bendOffset ?? 0}
                    disabled={viewOnly}
                    onChange={(e) =>
                      updateSelectedEdge({
                        routing: {
                          ...(selectedEdge.data as any)?.routing,
                          mode: "manual",
                          bendOffset: Number(e.target.value),
                        },
                      })
                    }
                  />
                  <input
                    type="number"
                    className="w-20 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                    min={-400}
                    max={400}
                    value={(selectedEdge.data as any)?.routing?.bendOffset ?? 0}
                    disabled={viewOnly}
                    onChange={(e) =>
                      updateSelectedEdge({
                        routing: {
                          ...(selectedEdge.data as any)?.routing,
                          mode: "manual",
                          bendOffset: Number(e.target.value),
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <label className="text-xs text-[var(--color-muted)]">Linienstil</label>
        <select
          className="w-full mt-1 mb-3 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
          value={(selectedEdge.data as any)?.style || "solid"}
          disabled={viewOnly}
          onChange={(e) => updateSelectedEdge({ style: e.target.value as any })}
        >
          <option value="solid">Durchgehend</option>
          <option value="dashed">Gestrichelt</option>
        </select>
        {!viewOnly && (
          <button
            className="w-full mt-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
            onClick={() => {
              if (!primarySelectedEdgeId) return;
              setConfirmDialog({
                title: "Edge wirklich löschen?",
                description: "Diese Kante wird endgültig entfernt.",
                confirmLabel: "Löschen",
                cancelLabel: "Abbrechen",
                tone: "danger",
                onConfirm: () => {
                  setRfEdges((eds: any) => eds.filter((e: any) => e.id !== primarySelectedEdgeId));
                  setSelectedEdgeIds([]);
                  setDirty(true);
                },
              });
            }}
          >
            Edge löschen
          </button>
        )}
      </div>
    );
  }

  if (!selectedNode) return null;

  return (
    <div className="w-80 border-l border-[var(--color-border)] dark:border-slate-800 p-4 bg-[var(--color-panel)] backdrop-blur space-y-4 h-[calc(100vh-64px)] overflow-y-auto">
      <div className="grid grid-cols-2 gap-2">
        {inspectorTabs.map((tab) => (
          <button
            key={tab.id}
            className={`w-full px-3 py-2 rounded-lg text-xs font-semibold border transition-colors text-left ${
              inspectorTab === tab.id
                ? "bg-[var(--color-accent)] text-white border-[var(--color-accent)]"
                : "border-[var(--color-border)] text-[var(--color-muted)] bg-white/60 dark:bg-slate-800/60"
            }`}
            onClick={() => setInspectorTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {inspectorTab === "general" && currentNodeData && (
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[var(--color-muted)]">Name</label>
            <input
              className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
              value={currentNodeData.label}
              disabled={viewOnly}
              onChange={(e) => updateDraftNode((prev) => ({ ...prev, label: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted)]">Beschreibung</label>
            <textarea
              className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
              value={currentNodeData.description || ""}
              disabled={viewOnly}
              onChange={(e) => updateDraftNode((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          {!structureTypeConfig.features?.contacts && (
            <>
              <div>
                <label className="text-xs text-[var(--color-muted)]">Dauer (geschätzt)</label>
                <input
                  className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                  placeholder="z.B. 2 Stunden, 3 Tage"
                  value={currentNodeData.custom_data?.duration || ""}
                  disabled={viewOnly}
                  onChange={(e) =>
                    updateDraftNode((prev) => ({
                      ...prev,
                      custom_data: { ...(prev.custom_data || {}), duration: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-[var(--color-muted)]">Verantwortlich</label>
                <input
                  className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
                  placeholder="Name oder Rolle"
                  value={currentNodeData.custom_data?.responsible || ""}
                  disabled={viewOnly}
                  onChange={(e) =>
                    updateDraftNode((prev) => ({
                      ...prev,
                      custom_data: { ...(prev.custom_data || {}), responsible: e.target.value },
                    }))
                  }
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-2 text-sm flex-wrap">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={currentNodeData.inactive}
                disabled={viewOnly}
                onChange={(e) => updateDraftNode((prev) => ({ ...prev, inactive: e.target.checked }))}
              />
              Inaktiv
            </label>
            {structureTypeConfig.features?.contacts && (
              <>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentNodeData.constituted}
                    disabled={viewOnly}
                    onChange={(e) => updateDraftNode((prev) => ({ ...prev, constituted: e.target.checked }))}
                  />
                  Konstituiert
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={currentNodeData.collapsed}
                    disabled={viewOnly}
                    onChange={(e) => updateDraftNode((prev) => ({ ...prev, collapsed: e.target.checked }))}
                  />
                  Collapsed
                </label>
              </>
            )}
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted)]">Typ</label>
            <select
              className="w-full mt-1 rounded-md border border-[var(--color-border)] bg-white dark:bg-slate-800 px-3 py-2"
              value={currentNodeData.type}
              disabled={viewOnly}
              onChange={(e) => {
                const t = nodeTypeById(e.target.value);
                const newData = {
                  ...currentNodeData,
                  type: e.target.value,
                  color: t?.defaultColor || currentNodeData.color,
                  text_color: t?.defaultTextColor || currentNodeData.text_color,
                };
                updateDraftNode(() => newData);
                updateSelectedNode(newData);
              }}
            >
              {nodeTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted)]">Form</label>
            <div className="mt-1">
              <ShapePicker
                value={currentNodeData.shape || "rectangle"}
                onChange={(shape) => {
                  const newData = { ...currentNodeData, shape };
                  updateDraftNode(() => newData);
                  updateSelectedNode(newData);
                }}
                disabled={viewOnly}
                compact
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[var(--color-muted)]">Farbe</label>
              <input
                type="color"
                className="w-full h-10 mt-1"
                value={currentNodeData.color || "#2563eb"}
                disabled={viewOnly}
                onChange={(e) => updateDraftNode((prev) => ({ ...prev, color: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-muted)] flex items-center justify-between">
                <span>Textfarbe</span>
                <span className="flex items-center gap-1 text-[11px]">
                  <input
                    type="checkbox"
                    checked={!currentNodeData.text_color}
                    disabled={viewOnly}
                    onChange={(e) =>
                      updateDraftNode((prev) => ({
                        ...prev,
                        text_color: e.target.checked ? null : prev.text_color || "#ffffff",
                      }))
                    }
                  />
                  <span>Automatisch</span>
                </span>
              </label>
              <input
                type="color"
                className="w-full h-10 mt-1"
                value={currentNodeData.text_color || "#ffffff"}
                disabled={viewOnly || !currentNodeData.text_color}
                onChange={(e) => updateDraftNode((prev) => ({ ...prev, text_color: e.target.value }))}
              />
            </div>
          </div>
        </div>
      )}

      {inspectorTab === "contacts" && currentNodeData && !viewOnly && (
        <div className="space-y-3">
          <ContactPicker
            structureId={structureId}
            selectedContactIds={currentNodeData.contact_ids || []}
            contactRoles={currentNodeData.contact_roles || {}}
            onChange={(contactIds, contactRoles) => {
              updateDraftNode((prev) => ({ ...prev, contact_ids: contactIds, contact_roles: contactRoles }));
            }}
          />
        </div>
      )}
      {inspectorTab === "contacts" && currentNodeData && viewOnly && (
        <ContactsViewer
          structureId={structureId}
          contactIds={currentNodeData.contact_ids || []}
          contactRoles={currentNodeData.contact_roles || {}}
        />
      )}

      {inspectorTab === "links" && currentNodeData && (
        <div className="space-y-2">
          {(currentNodeData.links || []).map((link, idx) => (
            <div
              key={link.id || idx}
              className="space-y-1 border border-[var(--color-border)] dark:border-slate-700 p-2 rounded-md bg-white/70 dark:bg-slate-900/40"
            >
              <input
                className="w-full rounded-md border border-[var(--color-border)] dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                placeholder="URL"
                value={link.url}
                disabled={viewOnly}
                onChange={(e) => {
                  const next = [...(currentNodeData.links || [])];
                  next[idx] = { ...link, url: e.target.value };
                  updateDraftNode((prev) => ({ ...prev, links: next }));
                }}
              />
              <input
                className="w-full rounded-md border border-[var(--color-border)] dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                placeholder="Titel"
                value={link.title || ""}
                disabled={viewOnly}
                onChange={(e) => {
                  const next = [...(currentNodeData.links || [])];
                  next[idx] = { ...link, title: e.target.value };
                  updateDraftNode((prev) => ({ ...prev, links: next }));
                }}
              />
              <textarea
                className="w-full rounded-md border border-[var(--color-border)] dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                placeholder="Beschreibung"
                value={link.description || ""}
                disabled={viewOnly}
                onChange={(e) => {
                  const next = [...(currentNodeData.links || [])];
                  next[idx] = { ...link, description: e.target.value };
                  updateDraftNode((prev) => ({ ...prev, links: next }));
                }}
              />
              {!viewOnly && (
                <button
                  className="text-xs text-red-500"
                  onClick={() => {
                    const next = [...(currentNodeData.links || [])];
                    next.splice(idx, 1);
                    updateDraftNode((prev) => ({ ...prev, links: next }));
                  }}
                >
                  Entfernen
                </button>
              )}
            </div>
          ))}
          {!viewOnly && (
            <button
              className="btn btn-secondary w-full justify-center"
              onClick={() =>
                updateDraftNode((prev) => ({
                  ...prev,
                  links: [...(prev.links || []), { id: uuid(), url: "https://", title: "", description: "" }],
                }))
              }
            >
              Link hinzufügen
            </button>
          )}
        </div>
      )}

      {inspectorTab === "notes" && currentNodeData && (
        <div className="space-y-2">
          {(currentNodeData.notes || []).map((note, idx) => (
            <textarea
              key={note.id || idx}
              className="w-full rounded-md border border-[var(--color-border)] dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
              value={note.text}
              disabled={viewOnly}
              onChange={(e) => {
                const next = [...(currentNodeData.notes || [])];
                next[idx] = { ...note, text: e.target.value };
                updateDraftNode((prev) => ({ ...prev, notes: next }));
              }}
            />
          ))}
          {!viewOnly && (
            <button
              className="btn btn-secondary w-full justify-center"
              onClick={() =>
                updateDraftNode((prev) => ({ ...prev, notes: [...(prev.notes || []), { id: uuid(), text: "" }] }))
              }
            >
              Notiz hinzufügen
            </button>
          )}
        </div>
      )}

      {!viewOnly && currentNodeData && (
        <div className="pt-2 flex gap-2 items-center">
          <button
            className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
              draftDirty
                ? "bg-[var(--color-accent)] text-white hover:opacity-90"
                : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500 cursor-not-allowed"
            }`}
            disabled={!draftDirty}
            onClick={applyDraftNode}
          >
            Änderungen speichern
          </button>
          {draftDirty && (
            <button
              className="text-xs text-[var(--color-muted)] underline"
              onClick={() => {
                if (!selectedNode) return;
                const raw = selectedNode.data as NodeData;
                const ensureContacts = (raw.contacts || []).map((c) => ({ id: c.id || uuid(), ...c }));
                const ensureLinks = (raw.links || []).map((l) => ({ id: l.id || uuid(), ...l }));
                const ensureNotes = (raw.notes || []).map((n) => ({ id: n.id || uuid(), ...n }));
                setDraftNode({ ...raw, contacts: ensureContacts, links: ensureLinks, notes: ensureNotes });
                setDraftDirty(false);
              }}
            >
              Verwerfen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
